import _ from 'lodash';
import moment from 'moment';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionButton,
  ActionsBanner,
  ButtonBadge,
  FiltersBar,
  Page,
  PeriodFilter,
  SplitButton,
  YesNoFilter,
} from '~/components';
import {
  ApproverFilter,
  ClientFiltersBar,
  ClientFiltersGroup,
  FilterButton,
  FiltersDrawer,
  MemberFiltersBar,
  MemberFiltersGroup,
  ProjectFiltersBar,
  ProjectFiltersGroup,
  TaskFiltersBar,
  TaskFiltersGroup,
  TimeFiltersBar,
  TimeFiltersGroup,
} from '~/components/filters';
import { useApi, useConfirmation, useSubscription, useToast, useWorkspace } from '~/contexts';
import {
  useAuth,
  useClientFilters,
  useDocumentTitle,
  useForm,
  useIsMounted,
  useMemberFilters,
  useProjectFilters,
  useProjectRoleFilters,
  useSearchParams,
  useSearchParamsConfig,
  useTimeFilters,
} from '~/hooks';
import useTaskFilters from '~/hooks/useTaskFilters';
import { PageLoader } from '~/routes/public/pages';
import { dateFormats, intervalOptions, QuerySort } from '~/utils';
import intervalsByScope from '~/utils/intervalsByScope';
import EditTimeEntry from '../edit-time-entry';
import ViewTimeEntry from '../view-time-entry';
import RejectTimeDialog from './RejectTimeDialog';
import SetToApprovedConfirmation from './SetToApprovedConfirmation';
import SetToRejectedConfirmation from './SetToRejectedConfirmation';
import TimeApprovalResults from './TimeApprovalResults';

function TimeApprovalsPage() {
  const documentTitle = useDocumentTitle('Time Approvals');

  const { workspace } = useWorkspace();
  const auth = useAuth();

  const isMounted = useIsMounted();

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const roleFilters = useProjectRoleFilters();
  const taskFilters = useTaskFilters();
  const memberFilters = useMemberFilters();
  const timeFilters = useTimeFilters();

  const [query, setQuery] = useState({
    isReady: false,
    searchParamsStatus: 'pending',
    data: null,
    params: {
      period: null,
      currency: workspace.currency,
      sort: new QuerySort('date', 'asc'),
      approver: workspace.member,
      includeLockedItems: 'no',
      showNotes: 'no',
      ...clientFilters.filters,
      ...projectFilters.filters,
      ...memberFilters.filters,
      ...taskFilters.filters,
      ...roleFilters.filters,
      ..._.omit(timeFilters.filters, ['timeApprovalStatuses']),
    },
    action: 'load',
  });

  const refetch = () => {
    setQuery((state) => ({
      ...state,
      action: 'refetch',
    }));
  };

  const setParams = (params) => {
    setQuery((state) => ({
      ...state,
      action: 'filter',
      params: { ...state.params, ...params },
      searchParamsStatus: 'ready',
    }));
  };

  const updateItems = (items) => {
    setQuery((state) => ({
      ...state,
      data: state.data
        .map((result) => {
          let item = items.find((i) => i.id === result.id);
          if (!item) return result;

          item = _.pick(
            item,
            'date',
            'isActuallyBillable',
            'minutes',
            'notes',
            'project',
            'role',
            'statusId',
            'task',
            'timeOffType',
            'timerStartedAt',
          );

          return { ...result, ...item };
        })
        // Remove items from the queue if the status doesn't match the filter. This is mostly to
        // clear the queue when an approval action is taken.
        // This may eventually require a refetch, to exclude items based on other properties
        // (which may have changed when using the time entry drawer).
        .filter((result) => {
          return !state.params?.statusId || result.statusId === state.params?.statusId;
        }),
    }));
  };

  const removeItem = (id) => {
    setQuery((state) => ({
      ...state,
      data: state.data.filter((i) => i.id !== id),
    }));
  };

  const { action, data, isReady, params, searchParamsStatus } = query;
  const [selection, setSelection] = useState([]);
  const [{ isSubmitting, saved }, form] = useForm();
  const [drawer, setDrawer] = useState(null);
  const api = useApi();
  const toast = useToast();
  const confirmation = useConfirmation();
  const { notify } = useSubscription();

  const searchParamsConfig = useSearchParamsConfig();
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: {
          ...searchParamsConfig.period,
          default: {
            ...intervalOptions.custom,
            start: moment().subtract(2, 'months').startOf('month').format(dateFormats.isoDate),
            end: moment().add(1, 'months').endOf('month').format(dateFormats.isoDate),
          },
        },
        currency: searchParamsConfig.currency,
        sort: { default: new QuerySort('date', 'asc'), ...searchParamsConfig.sort },
        approver: {
          default: workspace.member,
          ...searchParamsConfig.approver,
          deserialize: auth.workspace.manage
            ? searchParamsConfig.approver.deserialize
            : (value) => searchParamsConfig.approver.deserialize(value) ?? workspace.member,
        },
        includeLockedItems: { default: 'no', valid: ['yes', 'no'] },
        showNotes: { default: 'no', valid: ['yes', 'no'] },
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...memberFilters.searchParamsConfig,
        ...taskFilters.searchParamsConfig,
        ..._.omit(timeFilters.searchParamsConfig, ['timeApprovalStatuses']),
        ...roleFilters.searchParamsConfig,
      }),
      [
        workspace.member,
        searchParamsConfig,
        auth.workspace.manage,
        clientFilters,
        projectFilters,
        memberFilters,
        taskFilters,
        timeFilters,
        roleFilters,
      ],
    ),
    sessionKey: 'time_approvals',
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });

  useEffect(() => {
    if (searchParamsStatus !== 'pending') return;
    searchParams.get().then((params) => {
      if (params) setParams(params);
    });
  }, [searchParams, searchParamsStatus]);

  const urlSearchParams = useMemo(() => {
    return {
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      currency: params.currency ?? undefined,
      sort: params.sort,
      approverId: params.approver?.id,
      includeLockedItems: params.includeLockedItems ?? undefined,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...memberFilters.mapUrlSearchParams(params),
      ...taskFilters.mapUrlSearchParams(params),
      ...timeFilters.mapUrlSearchParams(params),
      ...roleFilters.mapUrlSearchParams(params),
    };
  }, [params, clientFilters, projectFilters, memberFilters, taskFilters, timeFilters, roleFilters]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).timeAdmin().getApprovals(urlSearchParams);

      if (!isMounted.current) return;

      setQuery((state) => ({
        ...state,
        isReady: true,
        action: null,
        data,
      }));
      return data;
    } catch (error) {
      setQuery((state) => ({ ...state, data: [] }));
      toast.error(error.message);
    }
  }, [workspace.id, api, isMounted, toast, urlSearchParams]);

  const refetchData = async () => {
    refetch();
    const data = await fetchData();
    const ids = data.map(({ id }) => id);
    setSelection(selection.filter((s) => ids.includes(s)));
  };

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleFilterChange = (values) => {
    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSelectionChange = (selection) => {
    setSelection(selection);
  };

  const handleEntryStatusChange = async (entry, statusId) => {
    try {
      let notes;
      if (statusId === 'rejected') {
        notes = await confirmation.prompt((resolve) => (
          <RejectTimeDialog count={1} onResolve={(notes) => resolve(notes)} />
        ));
        if (!notes) return;
      }

      form.submit(entry.id);

      const { data } = await api.www
        .workspaces(workspace.id)
        .timeAdmin()
        .batchUpdateStatus({ ids: [entry.id], statusId, notes });

      updateItems(data);
      refetchData();

      notify(useSubscription.keys.refresh_time_approval_count);
    } catch (error) {
      toast.error(error.message);
    }

    form.done();
  };

  const handleBatchStatusChange = async (statusId) => {
    try {
      let notes;
      if (statusId === 'rejected') {
        notes = await confirmation.prompt((resolve) => (
          <RejectTimeDialog count={selection.length} onResolve={(notes) => resolve(notes)} />
        ));
        if (!notes) return;
      }

      form.submit('batch');

      const { data } = await api.www
        .workspaces(workspace.id)
        .timeAdmin()
        .batchUpdateStatus({ ids: selection, statusId, notes });

      updateItems(data);
      refetchData();

      toast.success(`${{ approved: 'Approved', rejected: 'Rejected' }[statusId]} ${data.length} time entries.`);
      setSelection([]);
      form.save();
      notify(useSubscription.keys.refresh_time_approval_count);
    } catch (error) {
      toast.error(error.message);
      form.done();
    }
  };

  const handleGroupAction = async (group, statusId) => {
    const ids = group.entries.map((entry) => entry.id);

    try {
      let groupActions;

      switch (statusId) {
        case 'rejected': {
          let notes;
          if (statusId === 'rejected') {
            notes = await confirmation.prompt((resolve) => (
              <RejectTimeDialog count={ids.length} onResolve={(notes) => resolve(notes)} />
            ));
            if (!notes) return;
          }

          groupActions = { ids: group.entries.map((entry) => entry.id), statusId, notes };
          break;
        }

        default:
          groupActions = { ids: group.entries.map((entry) => entry.id), statusId };
      }

      form.submit({ action: 'group', group });

      const { data } = await api.www.workspaces(workspace.id).timeAdmin().batchUpdateStatus(groupActions);

      updateItems(data);
      refetchData();

      toast.success(`${{ approved: 'Approved', rejected: 'Rejected' }[statusId]} ${data.length} time entries.`);
      notify(useSubscription.keys.refresh_time_approval_count);
    } catch (error) {
      toast.error(error.message);
    }

    form.done();
  };

  const handleSetToApproved = async () => {
    await confirmation.prompt((resolve) => (
      <SetToApprovedConfirmation
        count={selection.length}
        onResolve={async (result) => {
          if (!result) {
            resolve(false);
            return;
          }

          try {
            const { data } = await api.www
              .workspaces(workspace.id)
              .timeAdmin()
              .setStatus({ ids: selection, statusId: 'approved' });

            await refetchData();

            toast.success(`Set ${data.length} time ${pluralize('entry', data.length)} to Approved.`);
            setSelection([]);
            notify(useSubscription.keys.refresh_time_approval_count);
            resolve(true);
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        }}
      />
    ));
  };

  const handleSetToRejected = async () => {
    await confirmation.prompt((resolve) => (
      <SetToRejectedConfirmation
        count={selection.length}
        onResolve={async (notes) => {
          if (!notes) {
            resolve(false);
            return;
          }

          try {
            const { data } = await api.www
              .workspaces(workspace.id)
              .timeAdmin()
              .setStatus({ ids: selection, statusId: 'rejected', notes });

            await refetchData();

            toast.success(`Set ${data.length} time ${pluralize('entry', data.length)} to Rejected .`);
            setSelection([]);
            notify(useSubscription.keys.refresh_time_approval_count);
            resolve(true);
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        }}
      />
    ));
  };

  const handleResultClick = (entry, mode) => {
    setDrawer({ entry, mode });
  };

  const handleCloseDrawer = () => {
    setDrawer(null);
    documentTitle.set('Time Approvals');
  };

  const handleEntrySaved = (entry) => {
    updateItems([entry]);
    refetchData();
    notify(useSubscription.keys.refresh_timer);
  };

  const handleEntryDeleted = (entry) => {
    removeItem(entry.id);
    refetchData();
    handleSelectionChange(selection.filter((s) => s !== entry.id));
    notify(useSubscription.keys.refresh_timer);
  };

  const handleTimerChange = (entry) => {
    // Stop all timers for the member
    const runningTimers = data
      .filter((e) => !!e.timerStartedAt && e.member.id === entry.member.id)
      .map((e) => ({
        ...e,
        minutes: moment.duration(e.minutes, 'minutes').add(moment().diff(e.timerStartedAt)).asMinutes(),
        timerStartedAt: null,
      }));

    updateItems([...runningTimers, entry]);
    if (entry.timerStartedAt) setSelection(selection.filter((e) => e !== entry.id));
    notify(useSubscription.keys_refresh_timer);
  };

  // Actions are only enabled if it's an approver's queue.
  const isApproverQueue = !!params.approver;
  const isAdminQueue = !params.approver && auth.workspace.manage;

  if (!isReady) return <PageLoader />;

  return (
    <>
      <Page scrollable>
        <Page.Header>
          <Page.Info>
            <Page.Eyebrow>Time</Page.Eyebrow>
            <Page.Title>Time Approvals</Page.Title>
          </Page.Info>

          <Page.Actions>
            <FilterButton isOutline onClick={showFilters} />
          </Page.Actions>
        </Page.Header>

        <>
          <Page.Filters>
            <FiltersBar>
              <PeriodFilter
                name="period"
                placeholder="Date Range"
                intervals={intervalsByScope.day}
                value={params.period}
                onChange={({ target: { value } }) => handleFilterChange({ period: value })}
              />

              <ApproverFilter
                name="approver"
                value={params.approver}
                onChange={({ target: { value } }) => handleFilterChange({ approver: value })}
              />

              <ProjectFiltersBar filters={params} sticky={['projects']} onChange={handleFilterChange} />

              <MemberFiltersBar
                filters={params}
                sticky={['members', 'memberPractices']}
                onChange={handleFilterChange}
              />

              <TimeFiltersBar filters={params} sticky={['timeBillableTypes']} onChange={handleFilterChange} />

              <ClientFiltersBar filters={params} onChange={handleFilterChange} />

              <TaskFiltersBar filters={params} onChange={handleFilterChange} />

              <YesNoFilter
                icon="filter"
                name="includeLockedItems"
                placeholder="Include Locked Items"
                value={params.includeLockedItems}
                onChange={({ target: { value } }) => handleFilterChange({ includeLockedItems: value })}
              />

              <YesNoFilter
                icon="gear"
                name="showNotes"
                placeholder="Show Notes"
                value={params.showNotes}
                onChange={({ target: { value } }) => handleFilterChange({ showNotes: value })}
              />
            </FiltersBar>
          </Page.Filters>
          <Filters values={params} isOpen={filtersVisible} onApply={handleFilterChange} onClose={hideFilters} />
        </>

        <TimeApprovalResults
          results={data}
          selection={selection}
          showNotes={params.showNotes}
          isSubmitting={isSubmitting}
          isApproverQueue={isApproverQueue}
          isAdminQueue={isAdminQueue}
          onResultClick={handleResultClick}
          onSelectionChange={handleSelectionChange}
          onStatusChange={handleEntryStatusChange}
          onGroupAction={handleGroupAction}
          onChange={refetchData}
          action={action}
        />

        {selection.length > 0 && (
          <ActionsBanner>
            {isApproverQueue && (
              <SplitButton>
                <ActionButton
                  isLoading={isSubmitting === 'batch'}
                  ok={saved}
                  onClick={() => handleBatchStatusChange('approved')}>
                  Approve <ButtonBadge visible={!saved && isSubmitting !== 'batch'}>{selection.length}</ButtonBadge>
                </ActionButton>

                <SplitButton.Menu position="top" disabled={selection.length === 0}>
                  {({ setIsOpen }) => (
                    <SplitButton.Item onClick={() => setIsOpen(false) || handleBatchStatusChange('rejected')}>
                      Reject
                    </SplitButton.Item>
                  )}
                </SplitButton.Menu>
              </SplitButton>
            )}

            {isAdminQueue && (
              <SplitButton data-testid="admin-actions">
                <ActionButton isLoading={isSubmitting === 'batch'} ok={saved} onClick={() => handleSetToApproved()}>
                  Set to Approved{' '}
                  <ButtonBadge visible={!saved && isSubmitting !== 'batch'}>{selection.length}</ButtonBadge>
                </ActionButton>

                <SplitButton.Menu position="top" disabled={selection.length === 0}>
                  {({ setIsOpen }) => (
                    <SplitButton.Item onClick={() => setIsOpen(false) || handleSetToRejected()}>
                      Set to Rejected
                    </SplitButton.Item>
                  )}
                </SplitButton.Menu>
              </SplitButton>
            )}
          </ActionsBanner>
        )}
      </Page>

      {drawer &&
        {
          edit: () => (
            <EditTimeEntry
              id={drawer.entry.id}
              memberId={drawer.entry.member.id}
              onSubmit={(body) => api.www.workspaces(workspace.id).timeAdmin(drawer.entry.id).update(body)}
              onConfirmDelete={(id) => api.www.workspaces(workspace.id).timeAdmin(id).delete()}
              onSaved={handleEntrySaved}
              onDeleted={handleEntryDeleted}
              onClose={handleCloseDrawer}
              onTimerChange={handleTimerChange}
            />
          ),
          view: () => <ViewTimeEntry id={drawer.entry.id} onClose={handleCloseDrawer} />,
          viewAttachments: () => <ViewTimeEntry id={drawer.entry.id} onClose={handleCloseDrawer} tab="attachments" />,
        }[drawer.mode]()}
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <PeriodFilter
        name="period"
        placeholder="Date Range"
        intervals={intervalsByScope.day}
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ApproverFilter
        name="approver"
        value={filters.approver}
        onChange={({ target: { value } }) => handleFilter({ approver: value })}
      />

      <YesNoFilter
        icon="filter"
        name="includeLockedItems"
        placeholder="Include Locked Items"
        value={filters.includeLockedItems}
        onChange={({ target: { value } }) => handleFilter({ includeLockedItems: value })}
      />

      <YesNoFilter
        icon="gear"
        name="showNotes"
        placeholder="Show Notes"
        value={filters.showNotes}
        onChange={({ target: { value } }) => handleFilter({ showNotes: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <TaskFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <TimeFiltersGroup filters={filters} onChange={handleFilter} />
    </FiltersDrawer>
  );
}

export default TimeApprovalsPage;
